import { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { TariffType } from '../../../@types/model/transformer/tariffs/tariffs';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../@types/model/transformer/invoice/transformerInvoice';
import MaterialTable from '../../customComponents/materialTable/Table';
import AppFunctionsService from '../../../services/appFunctionsService';
import Tooltip from '@material-ui/core/Tooltip';
import StandardFab from '../../customComponents/button/StandardFab';
import AddIcon from '@material-ui/icons/Add';
import moment from 'moment';
import { DatePicker } from '@material-ui/pickers';
import FormControl from '@material-ui/core/FormControl';
import Icon from '@material-ui/core/Icon';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import generalFunctions from '../../../store/general/functions';
import lodash from 'lodash';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import TransformerInvoiceJournalDialog from './dialog/journal/JournalDialog';
import { Transitions } from '../../customComponents/animations/Transitions';
import { IUserSession } from '../../../@types/employee';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';
import TransformerGroupDropdown from '../../customComponents/selector/transformer/GroupSelector';
import { ITransformerGroup } from '../../../@types/model/transformer/transformerGroup';
import TransformerEditDialog from './dialog/invoice/InvoiceEditDialog';
import GeneralFunctions from '../../../store/general/functions';
import DeleteIcon from '@material-ui/icons/Delete';
import ProjectDialog from '../../customComponentV2/dialog/ProjectDialog';
import LPUCapture from './dialog/invoice/lpu/LPUCaptureDialog';
import ConfirmationDialog from '../../customComponentV2/dialog/ConfirmationDialog';
import SPUCapture from './SPUCapture';
import { firestore } from 'firebase';
import Screen from '../../customComponentV2/screen/Screen';
import React from 'react';

interface ITransformerListProps {
    session ?: IUserSession | null;
    tariffType : TariffType;
}

type ITransformerListComponentProps = ITransformerListProps & RouteComponentProps;

const TransformerCaptureList = (props : ITransformerListComponentProps) : React.ReactElement => {

    const [type, setType] = useState<TariffType | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [invoices, setInvoices] = useState<Array<ITransformerInvoice>>([]);

    const [startMonthDate, setStartMonthDate] = useState<moment.Moment>(moment.utc().startOf('month').subtract(3, 'months'));
    const [endMonthDate, setEndMonthDate] = useState<moment.Moment>(moment.utc().startOf('month'));
    
    const [accountNumber, setAccountNumber] = useState<string>('');
    const [epNumber, setEpNumber] = useState<string>('');
    const [group, setGroup] = useState<ITransformerGroup | null>(null);
    const [selectedInvoices, setSelectedInvoices] = useState<Array<ITransformerInvoice>>([]);
    const [invoiceToDelete, setInvoiceToDelete] = useState<ITransformerInvoice | null>(null);
    const [isSPUCaptureDialogOpen, setIsSPUCaptureDialogOpen] = useState<boolean>(false);
    const [isLPUCaptureDialogOpen, setIsLPUCaptureDialogOpen] = useState<boolean>(false);

    const maxMonthDate = moment.utc().endOf('month');

    useEffect(() => {
        setType(props.tariffType);
    }, [props.location.search]);

    useEffect(() => {
        const session = props.session;

        if (!type || !session) return;

        const unsubscribe = TransformerInvoiceHelper.collection()
            .where('type', '==', type)
            .where('monthDate', '>=', firestore.Timestamp.fromMillis(startMonthDate.valueOf()))
            .where('monthDate', '<=', firestore.Timestamp.fromMillis(endMonthDate.valueOf()))
            .onSnapshot(snapshot => {
                setIsLoading(true);
                
                const fetchedDocs : Array<ITransformerInvoice> = [];

                try {
                    snapshot.forEach((doc) => {
                        fetchedDocs.push(doc.data());
                    });

                    setIsLoading(false);
                } catch (err) {
                    generalFunctions.generalShowError(err, 'Error loading invoices.');
                }

                const activeInvoices = fetchedDocs.filter(x => x.isActive).sort((a, b) => b.monthDate - a.monthDate);

                setInvoices(activeInvoices);
            }, (err) => {
                generalFunctions.generalShowErrorSnackbar(err.message);
                setIsLoading(false);
                setInvoices([]);
            });
        
        return (() => unsubscribe());
    }, [
        type,
        props.session,
        startMonthDate,
        endMonthDate,
    ]);

    const onAddClick = () => {
        if (type === 'lpu') {
            setIsLPUCaptureDialogOpen(true);
        } else if (type === 'spu') {
            setIsSPUCaptureDialogOpen(true);
        }
    };

    const onStartMonthDateChanged = (momentDate : moment.Moment | null) => {
        if (!momentDate) 
            return;

        setStartMonthDate(moment.utc(momentDate.valueOf()).startOf('month'));
        setSelectedInvoices([]);
    };

    const onEndMonthDateChanged = (momentDate : moment.Moment | null) => {
        if (!momentDate) 
            return;

        setEndMonthDate(moment.utc(momentDate.valueOf()).endOf('month'));
        setSelectedInvoices([]);
    };

    const onEPNumberChange = (value ?: ITransformer) => {
        setEpNumber(value?.EPNumber ?? '');
        setSelectedInvoices([]);
    };

    const onAccountNumberChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setAccountNumber(event.currentTarget.value);
        setSelectedInvoices([]);
    };

    const onGroupChange = (value ?: ITransformerGroup) => {
        setGroup(value ?? null);
        setSelectedInvoices([]);
    };

    const onJournalDialogClose = () => {
        setSelectedInvoices([]);
    };

    const onInvoiceDelete = (row : ITransformerInvoice) => {
        setInvoiceToDelete(row);
    };

    const handleInvoiceDelete = async () => {
        if (!props.session?.employee) return;

        if (invoiceToDelete) {
            setIsLoading(true);

            const inactiveInvoice = TransformerInvoiceHelper.createInactiveSave(props.session, invoiceToDelete);

            try {
                await TransformerInvoiceHelper.saveMany([inactiveInvoice]);
                GeneralFunctions.generalShowSuccessSnackbar(`${inactiveInvoice.transformer.MeterType} Invoice inactivated successfully.`);
            } catch (e) {
                GeneralFunctions.generalShowErrorSnackbar(`An error occurred while inactivating ${inactiveInvoice.transformer.MeterType} Invoice.`);
            } finally {
                setIsLoading(false);
            }
        }

        setInvoiceToDelete(null);
    };

    const onCloseLPUCaptureDialog = () => {
        setIsLPUCaptureDialogOpen(false);
    };

    const onCloseSPUCaptureDialog = () => {
        setIsSPUCaptureDialogOpen(false);
    };

    const isSelectable = (row : ITransformerInvoice) : boolean => {
        return !!group &&
            !row.paid &&
            !row.journaled &&
            (row.group === group.name) &&
            !!row.transformer.Company &&
            (group.paymentEnabled !== undefined && group.paymentEnabled !== null && !group.paymentEnabled
                ? true
                : !!row.transformer.BankAccount);
    };

    const filteredData = useMemo<Array<ITransformerInvoice>>(() => {
        return lodash
            .chain(invoices)
            .filter(n => n.accountNumber.toLowerCase().includes(accountNumber.toLowerCase()))
            .filter(n => epNumber ? n.epNumber.toLowerCase().includes(epNumber.toLowerCase()) : true)
            .filter(n => !group || n.group.toLowerCase().includes(group.name.toLowerCase()))
            .filter(n => n.monthDate >= startMonthDate.valueOf())
            .value();
    }, [invoices, startMonthDate, accountNumber, epNumber, group]);

    const onSelectionChange = (selectedRows : Array<ITransformerInvoice>) => {
        setSelectedInvoices(selectedRows);
    };

    return (
        <Screen
            useAnimatedLoader
            isLoading={isLoading}
            isPadded={false}
            isScrollable={false}>
            <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
                <div className={'flx1 fdc mh0 mw0'}>
                    <Toolbar disableGutters>
                        <div className='flx1 ais mb10'>
                            {
                                !!selectedInvoices.length &&
                                !!type &&
                                <TransformerInvoiceJournalDialog
                                    onClose={onJournalDialogClose}
                                    invoices={selectedInvoices}
                                    type={type}
                                />
                            }
                        </div>
                        <div className='fdr mw900 ais'>
                            <div className={'flx1 ais mb10 pr10'}>
                                <FormControl fullWidth>
                                    <DatePicker value={startMonthDate}
                                        views={['month']}
                                        maxDate={maxMonthDate}
                                        onChange={onStartMonthDateChanged}
                                        format={'MMM YYYY'}
                                        label={'Select Start Month'}
                                        id={'startMonthDate'}
                                        className={'TextField'}
                                        margin={'normal'}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais mb10 pr10'}>
                                <FormControl fullWidth>
                                    <DatePicker
                                        value={endMonthDate}
                                        views={['month']}
                                        maxDate={maxMonthDate}
                                        onChange={onEndMonthDateChanged}
                                        format={'MMM YYYY'}
                                        error={endMonthDate.isBefore(startMonthDate)}
                                        label={
                                            endMonthDate.isBefore(startMonthDate)
                                                ? 'End month cannot be before start month'
                                                : 'Select End Month'
                                        }
                                        id={'endMonthDate'}
                                        className={'TextField'}
                                        margin={'normal'}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais mb10 pl10 pr10'}>
                                <FormControl fullWidth>
                                    <TextField
                                        autoComplete='off'
                                        id='accountNumber'
                                        label='Account No'
                                        value={accountNumber}
                                        onChange={onAccountNumberChange}
                                        margin='normal'
                                        className={'TextField'}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais mb10 pl10 pr10 mt16'}>
                                <FormControl fullWidth>
                                    <TransformerDropdown
                                        value={epNumber}
                                        label='Waypoint No'
                                        fullWidth
                                        onChange={onEPNumberChange}
                                        type={type ?? undefined}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais mb10 pl10 pr10 mt16'}>
                                <TransformerGroupDropdown
                                    fullWidth
                                    value={group?.name}
                                    onChange={onGroupChange}
                                    type={type?.toLocaleUpperCase()}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    </Toolbar>
                    <Card className={'flx1 fdc mb70'}>
                        <MaterialTable<ITransformerInvoice>
                            id='transformerInvoicesTable'
                            data={filteredData}
                            rowsPerPage={100}
                            externalSort
                            selectable
                            selectedRows={selectedInvoices}
                            onSelectionChanged={onSelectionChange}
                            isSelectable={isSelectable}
                            columns={[{
                                header: '',
                                field: 'monthDate',
                                width: 75,
                                paddingLeft: 15,
                                renderCell: row => (
                                    <div className='aic'>
                                        {
                                            !row.journaled &&
                                            <TransformerEditDialog invoice={row} />
                                        }
                                        {
                                            !row.journaled &&
                                            <Tooltip title={!row.isActive ? 'Already Inactive' : 'Delete'}>
                                                <div>
                                                    <IconButton
                                                        className={`${row.isActive ? 'cr' : 'cg4'}`}
                                                        onClick={() => onInvoiceDelete(row)}
                                                        disabled={!row.isActive}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            </Tooltip>
                                        }
                                    </div>
                                ),
                            }, {
                                header: 'Month',
                                field: 'monthDate',
                                width: 75,
                                paddingLeft: 15,
                                renderCell: row => (
                                    <div className='aic'>
                                        {AppFunctionsService.formatDateTimeToMonthYearOnly(row.monthDate)}
                                    </div>
                                ),
                            }, {
                                header: 'Way Point',
                                field: 'epNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Group',
                                field: 'group',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Pole No',
                                field: 'poleNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Account No',
                                field: 'accountNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Total Usage',
                                field: 'totalUsage',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Days',
                                field: 'days',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Total Charge',
                                field: 'totalCharge',
                                width: 165,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {(row.totalCharge).toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'Interest',
                                field: 'totalInterest',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {row.totalInterest.toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'Credit',
                                field: 'credit',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {row.credit.toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'VAT',
                                field: 'vatCharge',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {row.vatCharge.toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'Total Due',
                                field: 'totalDue',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {(row.totalDue).toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'Total Billed',
                                field: 'totalDue',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <div className=''>
                                        {(row.totalBilled).toLocaleCurrency()}
                                    </div>
                                ),
                            }, {
                                header: 'Is Active?',
                                field: 'isActive',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => n.isActive ? (<Icon className='cdg'>check</Icon>) : (<Icon className='cr'>close</Icon>),
                            }, {
                                header: 'Journaled',
                                field: 'journaled',
                                width: 145,
                                renderCell: row => (
                                    <div className='fdc jcc'>
                                        <Tooltip title={!row.journaled ? '' : `${row.journaledByName} - ${row.journaledByEmployeeNumber}`}>
                                            <CheckCircleIcon className={!row.journaled ? 'cg2' : 'cp'} />
                                        </Tooltip>
                                    </div>
                                ),
                            }, {
                                header: 'Paid',
                                field: 'paid',
                                width: 145,
                                renderCell: row => (
                                    <div className='fdc jcc'>
                                        <Tooltip title={!row.paid ? '' : `${row.paidByName} - ${row.paidByEmployeeNumber}`}>
                                            <CheckCircleIcon className={!row.paid ? 'cg2' : 'cp'} />
                                        </Tooltip>
                                    </div>
                                ),
                            }, {
                                header: 'Created By',
                                field: 'createdByName',
                                width: 145,
                                renderCell: row => (
                                    <Tooltip title={row.createdByEmployee}>
                                        <div>
                                            {
                                                row.createdByName
                                            }
                                        </div>
                                    </Tooltip>
                                ),
                            }]}
                        />
                    </Card>
                    <div className='fdr jcfe'>
                        <StandardFab aria-label='add' disabled={isLoading} onClick={onAddClick}>
                            <AddIcon />
                        </StandardFab>
                    </div>
                </div>
                <ConfirmationDialog 
                    open={!!invoiceToDelete}
                    isLoading={isLoading}
                    title={'Delete Invoice'}
                    description={'Are you sure you want to inactivate the selected entry?'}
                    onAccept={handleInvoiceDelete}
                    onClose={() => setInvoiceToDelete(null)}
                    dialogType={'red'}
                />
                <ProjectDialog
                    isOpen={isLPUCaptureDialogOpen}
                    fullScreen
                    isLoadingCircular={isLoading}
                    onClose={onCloseLPUCaptureDialog}
                    title={'ADD LPU INVOICE'}
                >
                    <LPUCapture
                        onDismiss={onCloseLPUCaptureDialog}
                    />
                </ProjectDialog>
                <ProjectDialog
                    isOpen={isSPUCaptureDialogOpen}
                    fullScreen
                    isLoadingCircular={isLoading}
                    onClose={onCloseSPUCaptureDialog}
                    title={'ADD SPU INVOICE'}
                >
                    <SPUCapture
                        onDismiss={onCloseSPUCaptureDialog}
                    />
                </ProjectDialog>
            </div>
        </Screen>
    );
};

export default TransformerCaptureList;
